import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOffline } from '../context/OfflineContext';
import Header from '../components/Header';
import PhotoSlot from '../components/PhotoSlot';
import SignaturePad from '../components/SignaturePad';
import GalleryUploader from '../components/GalleryUploader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import { 
  ArrowLeft, 
  Save, 
  Send, 
  Loader2, 
  Clock,
  AlertTriangle,
  FileText,
  CheckCircle,
  Plus,
  Trash2,
  WifiOff,
  Download,
  ClipboardList,
  BookTemplate,
  MessageSquare,
  Images
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  draft: { label: 'Draft', class: 'status-draft', icon: FileText },
  submitted: { label: 'Submitted', class: 'status-submitted', icon: Clock },
  returned: { label: 'Returned', class: 'status-returned', icon: AlertTriangle },
  approved: { label: 'Approved', class: 'status-approved', icon: CheckCircle },
  rejected: { label: 'Rejected', class: 'status-rejected', icon: AlertTriangle },
};

export default function FormEditor() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isAdminOrManager } = useAuth();
  const { isOnline, queueSync, cacheForms, getCachedForms } = useOffline();
  const signatureRef = useRef(null);
  
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addingPage, setAddingPage] = useState(false);
  const [removingPage, setRemovingPage] = useState(false);
  const [currentPage, setCurrentPage] = useState('1');
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showRemovePageDialog, setShowRemovePageDialog] = useState(false);
  const [pageToRemove, setPageToRemove] = useState(null);
  const [offlineMode, setOfflineMode] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  // Form fields
  const [individualName, setIndividualName] = useState('');
  const [individualDate, setIndividualDate] = useState('');
  const [signatureData, setSignatureData] = useState(null);
  const [notes, setNotes] = useState('');
  const [pages, setPages] = useState([]);
  const [checklist, setChecklist] = useState({ items: Array(25).fill({ requirement: '', status: '' }) });
  const [building, setBuilding] = useState('');
  const [floorNumber, setFloorNumber] = useState('');
  const [flatNumber, setFlatNumber] = useState('');
  const [submittedByName, setSubmittedByName] = useState('');
  const [submittedDate, setSubmittedDate] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  
  // Admin action states
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  useEffect(() => {
    fetchForm();
  }, [formId, isOnline]);

  const fetchForm = async () => {
    try {
      if (isOnline) {
        const response = await axios.get(`${API}/forms/${formId}`);
        const formData = response.data;
        setForm(formData);
        setIndividualName(formData.individual_name || '');
        setIndividualDate(formData.individual_date || '');
        setSignatureData(formData.signature_data);
        setNotes(formData.notes || '');
        setPages(formData.pages || []);
        setChecklist(formData.checklist || { items: Array(25).fill().map(() => ({ requirement: '', status: '' })) });
        setBuilding(formData.building || '');
        setFloorNumber(formData.floor_number || '');
        setFlatNumber(formData.flat_number || '');
        setSubmittedByName(formData.submitted_by_name || formData.created_by_name || '');
        setSubmittedDate(formData.submitted_date || '');
        setOfflineMode(false);
      } else {
        // Try to load from cache when offline
        const cachedForms = await getCachedForms();
        const cachedForm = cachedForms.find(f => f.form_id === formId);
        if (cachedForm) {
          setForm(cachedForm);
          setIndividualName(cachedForm.individual_name || '');
          setIndividualDate(cachedForm.individual_date || '');
          setSignatureData(cachedForm.signature_data);
          setNotes(cachedForm.notes || '');
          setPages(cachedForm.pages || []);
          setChecklist(cachedForm.checklist || { items: Array(25).fill().map(() => ({ requirement: '', status: '' })) });
          setBuilding(cachedForm.building || '');
          setFloorNumber(cachedForm.floor_number || '');
          setFlatNumber(cachedForm.flat_number || '');
          setSubmittedByName(cachedForm.submitted_by_name || cachedForm.created_by_name || '');
          setSubmittedDate(cachedForm.submitted_date || '');
          setOfflineMode(true);
        } else {
          toast.error('Form not available offline');
          navigate('/dashboard');
        }
      }
    } catch (error) {
      // Try cache on error
      try {
        const cachedForms = await getCachedForms();
        const cachedForm = cachedForms.find(f => f.form_id === formId);
        if (cachedForm) {
          setForm(cachedForm);
          setIndividualName(cachedForm.individual_name || '');
          setIndividualDate(cachedForm.individual_date || '');
          setSignatureData(cachedForm.signature_data);
          setNotes(cachedForm.notes || '');
          setPages(cachedForm.pages || []);
          setOfflineMode(true);
          toast.info('Loaded from offline cache');
        } else {
          toast.error('Failed to load form');
          navigate('/dashboard');
        }
      } catch (cacheError) {
        toast.error('Failed to load form');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const canEdit = () => {
    if (!form) return false;
    if (isAdminOrManager()) return true;
    if (form.created_by !== user?.user_id) return false;
    return form.status === 'draft' || form.status === 'returned';
  };

  const handleSlotImageChange = (pageIndex, slotIndex, imageData) => {
    setPages(prev => {
      const newPages = [...prev];
      newPages[pageIndex] = {
        ...newPages[pageIndex],
        slots: newPages[pageIndex].slots.map((slot, idx) => 
          idx === slotIndex ? { ...slot, image_data: imageData } : slot
        )
      };
      return newPages;
    });
  };

  const handleSlotDescriptionChange = (pageIndex, slotIndex, description) => {
    setPages(prev => {
      const newPages = [...prev];
      newPages[pageIndex] = {
        ...newPages[pageIndex],
        slots: newPages[pageIndex].slots.map((slot, idx) => 
          idx === slotIndex ? { ...slot, description } : slot
        )
      };
      return newPages;
    });
  };

  const handleGalleryUpload = (pageIndex, imageDataArray) => {
    setPages(prev => {
      const newPages = [...prev];
      const currentPage = newPages[pageIndex];
      
      // Find empty slots and fill them with uploaded images
      let imageIndex = 0;
      const updatedSlots = currentPage.slots.map((slot) => {
        // If slot is empty and we have more images to add
        if (!slot.image_data && imageIndex < imageDataArray.length) {
          const newSlot = { ...slot, image_data: imageDataArray[imageIndex] };
          imageIndex++;
          return newSlot;
        }
        return slot;
      });
      
      newPages[pageIndex] = {
        ...currentPage,
        slots: updatedSlots
      };
      
      return newPages;
    });
  };

  // Handle admin comment on photo slot
  const handleAdminCommentChange = (pageIndex, slotIndex, comment) => {
    setPages(prev => {
      const newPages = [...prev];
      newPages[pageIndex] = {
        ...newPages[pageIndex],
        slots: newPages[pageIndex].slots.map((slot, idx) =>
          idx === slotIndex ? { ...slot, admin_comment: comment } : slot
        )
      };
      return newPages;
    });
  };

  // Count empty slots on current page
  const getEmptySlotsCount = (pageIndex) => {
    if (!pages[pageIndex]) return 0;
    return pages[pageIndex].slots.filter(slot => !slot.image_data).length;
  };

  const handleSave = async () => {
    setSaving(true);
    const formData = {
      form_id: formId,
      pages,
      checklist,
      individual_name: individualName,
      individual_date: individualDate,
      signature_data: signatureData,
      notes,
      building,
      floor_number: floorNumber,
      flat_number: flatNumber,
      submitted_by_name: submittedByName,
      submitted_date: submittedDate
    };
    
    try {
      if (isOnline) {
        await axios.put(`${API}/forms/${formId}`, formData);
        toast.success('Form saved successfully');
      } else {
        // Queue for sync and update local cache
        await queueSync('UPDATE_FORM', formData);
        
        // Update local cache
        const cachedForms = await getCachedForms();
        const updatedForms = cachedForms.map(f => 
          f.form_id === formId 
            ? { ...f, ...formData, updated_at: new Date().toISOString() }
            : f
        );
        await cacheForms(updatedForms);
        
        toast.info('Saved offline - will sync when online');
      }
    } catch (error) {
      // Try to save locally on error
      await queueSync('UPDATE_FORM', formData);
      toast.info('Saved locally - will sync when online');
    } finally {
      setSaving(false);
    }
  };

  // Checklist handlers
  const handleChecklistChange = (index, field, value) => {
    setChecklist(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      return { items: newItems };
    });
  };

  // Save as template (admin only)
  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    setSavingTemplate(true);
    try {
      await axios.post(`${API}/templates/from-form/${formId}?name=${encodeURIComponent(templateName)}&description=Created from form`);
      toast.success('Template saved successfully');
      setShowTemplateDialog(false);
      setTemplateName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleAddPage = async () => {
    if (pages.length >= 20) {
      toast.error('Maximum 20 pages allowed');
      return;
    }
    
    setAddingPage(true);
    try {
      // Save current changes first
      await axios.put(`${API}/forms/${formId}`, {
        pages,
        individual_name: individualName,
        individual_date: individualDate,
        signature_data: signatureData,
        notes
      });
      
      // Add new page
      const response = await axios.post(`${API}/forms/${formId}/add-page`);
      setPages(response.data.pages);
      setCurrentPage(String(response.data.pages.length));
      toast.success('New page added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add page');
    } finally {
      setAddingPage(false);
    }
  };

  const handleRemovePage = async () => {
    if (pages.length <= 1) {
      toast.error('Cannot remove the last page');
      return;
    }
    
    setRemovingPage(true);
    try {
      // Save current changes first
      await axios.put(`${API}/forms/${formId}`, {
        pages,
        individual_name: individualName,
        individual_date: individualDate,
        signature_data: signatureData,
        notes
      });
      
      // Remove page
      const response = await axios.delete(`${API}/forms/${formId}/pages/${pageToRemove}`);
      setPages(response.data.pages);
      
      // Adjust current page if needed
      if (parseInt(currentPage) > response.data.pages.length) {
        setCurrentPage(String(response.data.pages.length));
      }
      
      toast.success('Page removed');
      setShowRemovePageDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove page');
    } finally {
      setRemovingPage(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Save first
      await axios.put(`${API}/forms/${formId}`, {
        pages,
        individual_name: individualName,
        individual_date: individualDate,
        signature_data: signatureData,
        notes
      });
      
      // Then submit
      await axios.post(`${API}/forms/${formId}/submit`);
      toast.success('Form submitted successfully');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit form');
    } finally {
      setSubmitting(false);
      setShowSubmitDialog(false);
    }
  };

  // Admin action handlers
  const handleApproveForm = async () => {
    setProcessingAction(true);
    try {
      await axios.post(`${API}/forms/${formId}/approve`);
      toast.success('Form approved successfully');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve form');
    } finally {
      setProcessingAction(false);
      setShowApproveDialog(false);
    }
  };

  const handleReturnForm = async () => {
    if (!returnReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    setProcessingAction(true);
    try {
      await axios.post(`${API}/forms/${formId}/return`, { reason: returnReason });
      toast.success('Form returned to user');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to return form');
    } finally {
      setProcessingAction(false);
      setShowReturnDialog(false);
      setReturnReason('');
    }
  };

  const handleRejectForm = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    setProcessingAction(true);
    try {
      await axios.post(`${API}/forms/${formId}/reject`, { reason: rejectReason });
      toast.success('Form rejected');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject form');
    } finally {
      setProcessingAction(false);
      setShowRejectDialog(false);
      setRejectReason('');
    }
  };

  const handleExportPdf = async () => {
    if (!form) return;
    
    setGeneratingPdf(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;

      // ---- Brand Theme (Gray + Red) ----
      const COLOR_RED = [193, 18, 31];          // Brand red
      const COLOR_RED_DARK = [127, 29, 29];     // Deep red accent
      const COLOR_GRAY_900 = [17, 24, 39];      // Body text dark
      const COLOR_GRAY_700 = [55, 65, 81];      // Body text
      const COLOR_GRAY_500 = [107, 114, 128];   // Secondary text
      const COLOR_GRAY_300 = [209, 213, 219];   // Borders
      const COLOR_GRAY_100 = [243, 244, 246];   // Row alt fill
      const COLOR_GRAY_50  = [249, 250, 251];   // Page background
      const HEADER_BAND_H = 10;                  // Red top band height (mm)
      const FOOTER_BAND_H = 8;                   // Gray bottom band height (mm)
      
      // Get company logo if available
      let companyLogo = null;
      if (user?.company_id) {
        try {
          const companyRes = await axios.get(`${API}/companies/${user.company_id}`);
          companyLogo = companyRes.data?.logo_url;
        } catch (e) {
          console.log('Could not fetch company logo');
        }
      }
      
      // Get form title from Building/Floor/Flat
      const formTitle = [
        building || form.building,
        floorNumber || form.floor_number ? `Floor ${floorNumber || form.floor_number}` : '',
        flatNumber || form.flat_number ? `Flat ${flatNumber || form.flat_number}` : ''
      ].filter(Boolean).join(' / ') || form.company_name || form.project_name;
      
      const watermarkText = flatNumber || form.flat_number ? `Flat: ${flatNumber || form.flat_number}` : '';

      // Page chrome: gray page background + red top band + gray footer band.
      // Called at the start of every page so the theme is consistent.
      const drawPageChrome = (pageLabel) => {
        // Page background (subtle gray)
        pdf.setFillColor(...COLOR_GRAY_50);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');

        // Top red band
        pdf.setFillColor(...COLOR_RED);
        pdf.rect(0, 0, pageWidth, HEADER_BAND_H, 'F');
        // Thin dark red accent under band
        pdf.setFillColor(...COLOR_RED_DARK);
        pdf.rect(0, HEADER_BAND_H, pageWidth, 0.6, 'F');

        // Brand text on left of band
        pdf.setFontSize(9);
        pdf.setTextColor(255, 255, 255);
        pdf.text('QA BUILDER PRO', margin, HEADER_BAND_H - 3.2);

        // Page label on right of band
        if (pageLabel) {
          pdf.setFontSize(8);
          pdf.setTextColor(255, 255, 255);
          pdf.text(pageLabel, pageWidth - margin, HEADER_BAND_H - 3.2, { align: 'right' });
        }

        // Bottom gray footer band
        pdf.setFillColor(...COLOR_GRAY_100);
        pdf.rect(0, pageHeight - FOOTER_BAND_H, pageWidth, FOOTER_BAND_H, 'F');
        pdf.setFillColor(...COLOR_RED);
        pdf.rect(0, pageHeight - FOOTER_BAND_H, pageWidth, 0.6, 'F');
      };

      // Helper function to add watermark and logo at bottom of each page
      const addWatermark = () => {
        // Add company logo watermark if available
        if (companyLogo) {
          try {
            pdf.setGState(new pdf.GState({ opacity: 0.35 }));
            pdf.addImage(companyLogo, 'AUTO', pageWidth - 35, pageHeight - FOOTER_BAND_H - 18, 25, 15);
            pdf.setGState(new pdf.GState({ opacity: 1 }));
          } catch (e) {
            console.log('Could not add logo to PDF');
          }
        }

        // Footer text (centered in gray band)
        pdf.setFontSize(8);
        pdf.setTextColor(...COLOR_GRAY_700);
        const footerText = watermarkText
          ? `${watermarkText}  ·  qabuilderpro.co.uk`
          : 'qabuilderpro.co.uk';
        pdf.text(footerText, pageWidth / 2, pageHeight - FOOTER_BAND_H / 2 + 1, { align: 'center' });
      };
      
      // ==================== PAGE 1: COVER PAGE ====================
      drawPageChrome('Cover');
      let yPos = HEADER_BAND_H + 8;
      
      // Add company logo at top left if available
      if (companyLogo) {
        try {
          pdf.addImage(companyLogo, 'AUTO', margin, yPos, 28, 16);
          yPos += 20;
        } catch (e) {
          console.log('Could not add logo to PDF header');
        }
      }
      
      // Title (Building / Floor / Flat) — red
      pdf.setFontSize(18);
      pdf.setTextColor(...COLOR_RED);
      pdf.text(formTitle, pageWidth / 2, yPos, { align: 'center' });
      yPos += 7;

      // Red underline accent under title
      pdf.setDrawColor(...COLOR_RED);
      pdf.setLineWidth(0.6);
      const titleW = Math.min(pdf.getTextWidth(formTitle) + 10, pageWidth - margin * 2);
      pdf.line((pageWidth - titleW) / 2, yPos - 4, (pageWidth + titleW) / 2, yPos - 4);
      pdf.setLineWidth(0.2);
      
      // Trade type and form type
      pdf.setFontSize(11);
      pdf.setTextColor(...COLOR_GRAY_500);
      const formTypeText = form.form_type ? ` - ${form.form_type}` : '';
      pdf.text(`${form.trade_type}${formTypeText}  |  ${form.status.toUpperCase()}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
      
      // ---- LEFT SIDE: Form Details ----
      const leftColX = margin;
      const rightColX = pageWidth - margin - 55;
      let leftY = yPos;
      
      pdf.setFontSize(7);
      pdf.setTextColor(...COLOR_GRAY_700);
      
      // Form details on left (small writing)
      const detailsLeft = [
        `Company: ${form.company_name || form.project_name || 'N/A'}`,
        `Site: ${form.site_name || 'N/A'}`,
        `Building: ${building || form.building || '_______'}`,
        `Floor: ${floorNumber || form.floor_number || '_______'}`,
        `Flat/Unit: ${flatNumber || form.flat_number || '_______'}`,
        ``,
        `Created: ${format(new Date(form.timestamp), 'dd/MM/yyyy HH:mm')}`,
        `Submitted by: ${submittedByName || form.submitted_by_name || form.created_by_name || 'N/A'}`,
        `Date Submitted: ${submittedDate || form.submitted_date || individualDate || '_______'}`,
      ];
      
      detailsLeft.forEach(line => {
        pdf.text(line, leftColX, leftY);
        leftY += 3.5;
      });
      
      // ---- RIGHT SIDE: Signature Box ----
      pdf.setDrawColor(...COLOR_RED);
      pdf.setFillColor(255, 255, 255);
      pdf.setLineWidth(0.4);
      pdf.rect(rightColX, yPos, 55, 32, 'FD');
      pdf.setLineWidth(0.2);

      // Red accent stripe at top of signature box
      pdf.setFillColor(...COLOR_RED);
      pdf.rect(rightColX, yPos, 55, 1.2, 'F');

      pdf.setFontSize(8);
      pdf.setTextColor(...COLOR_RED_DARK);
      pdf.text('Signature', rightColX + 2, yPos + 5);
      
      if (signatureData) {
        try {
          pdf.addImage(signatureData, 'PNG', rightColX + 2, yPos + 7, 50, 22);
        } catch (e) {
          pdf.setTextColor(...COLOR_GRAY_500);
          pdf.text('[Signature]', rightColX + 20, yPos + 18);
        }
      }
      
      yPos = Math.max(leftY, yPos + 35) + 5;
      
      // ---- CHECKLIST TABLE ----
      pdf.setFontSize(10);
      pdf.setTextColor(...COLOR_RED);
      pdf.text('Requirements Checklist', margin, yPos);
      yPos += 5;
      
      const checklistItems = checklist?.items || [];
      const rowHeight = 5.5;
      const col1Width = 8;
      const col2Width = pageWidth - margin * 2 - col1Width - 18;
      const col3Width = 18;
      
      // Table header — red
      pdf.setFillColor(...COLOR_RED);
      pdf.rect(margin, yPos, col1Width + col2Width + col3Width, rowHeight, 'F');
      pdf.setFontSize(7);
      pdf.setTextColor(255, 255, 255);
      pdf.text('#', margin + 2, yPos + 4);
      pdf.text('Requirement', margin + col1Width + 2, yPos + 4);
      pdf.text('Status', margin + col1Width + col2Width + 2, yPos + 4);
      yPos += rowHeight;
      
      // Table rows (25 rows)
      pdf.setFontSize(6.5);
      
      for (let i = 0; i < 25; i++) {
        const item = checklistItems[i] || { requirement: '', status: '' };
        
        // Alternate row colors — gray
        if (i % 2 === 0) {
          pdf.setFillColor(...COLOR_GRAY_100);
          pdf.rect(margin, yPos, col1Width + col2Width + col3Width, rowHeight, 'F');
        } else {
          pdf.setFillColor(255, 255, 255);
          pdf.rect(margin, yPos, col1Width + col2Width + col3Width, rowHeight, 'F');
        }
        
        // Draw row borders
        pdf.setDrawColor(...COLOR_GRAY_300);
        pdf.rect(margin, yPos, col1Width, rowHeight);
        pdf.rect(margin + col1Width, yPos, col2Width, rowHeight);
        pdf.rect(margin + col1Width + col2Width, yPos, col3Width, rowHeight);
        
        // Row content
        pdf.setTextColor(...COLOR_GRAY_500);
        pdf.text(String(i + 1), margin + 2, yPos + 3.8);
        
        pdf.setTextColor(...COLOR_GRAY_900);
        pdf.text((item.requirement || '').substring(0, 70), margin + col1Width + 1, yPos + 3.8);
        
        // Status as tick/x/NA symbol
        const status = (item.status || '').toUpperCase().trim();
        let statusSymbol = '';
        if (status === '✓' || status === 'YES' || status === 'TICK' || status === 'Y' || status === 'PASS') {
          statusSymbol = '✓';
          pdf.setTextColor(34, 139, 34);
        } else if (status === '✗' || status === 'X' || status === 'NO' || status === 'N' || status === 'FAIL') {
          statusSymbol = '✗';
          pdf.setTextColor(...COLOR_RED);
        } else if (status === 'NA' || status === 'N/A') {
          statusSymbol = 'N/A';
          pdf.setTextColor(...COLOR_GRAY_500);
        } else {
          statusSymbol = item.status || '';
          pdf.setTextColor(...COLOR_GRAY_900);
        }
        
        pdf.setFontSize(8);
        pdf.text(statusSymbol, margin + col1Width + col2Width + 5, yPos + 4);
        pdf.setFontSize(6.5);
        
        yPos += rowHeight;
      }
      
      // Notes at bottom of first page if space
      if (notes && yPos < pageHeight - FOOTER_BAND_H - 25) {
        yPos += 5;
        pdf.setFontSize(8);
        pdf.setTextColor(...COLOR_RED);
        pdf.text('Notes', margin, yPos);
        yPos += 4;
        pdf.setFontSize(7);
        pdf.setTextColor(...COLOR_GRAY_700);
        const splitNotes = pdf.splitTextToSize(notes, pageWidth - margin * 2);
        pdf.text(splitNotes.slice(0, 3), margin, yPos);
      }
      
      addWatermark();
      
      // ==================== PHOTO PAGES (6 slots per page) ====================
      for (let pageIdx = 0; pageIdx < pages.length; pageIdx++) {
        pdf.addPage();
        drawPageChrome(`Photos · Page ${pageIdx + 1} of ${pages.length}`);
        addWatermark();

        yPos = HEADER_BAND_H + 6;

        // Page header (form context)
        pdf.setFontSize(9);
        pdf.setTextColor(...COLOR_RED);
        pdf.text(formTitle, margin, yPos);
        pdf.setTextColor(...COLOR_GRAY_500);
        pdf.text(form.trade_type, pageWidth - margin, yPos, { align: 'right' });

        // Thin red divider
        pdf.setDrawColor(...COLOR_RED);
        pdf.setLineWidth(0.4);
        pdf.line(margin, yPos + 2, pageWidth - margin, yPos + 2);
        pdf.setLineWidth(0.2);
        yPos += 7;
        
        const page = pages[pageIdx];
        const imgWidth = 88;
        const imgHeight = 52;
        const descHeight = 18;
        const slotHeight = imgHeight + descHeight + 6;
        
        // 2 columns x 3 rows = 6 slots per page
        for (let slotIdx = 0; slotIdx < 6; slotIdx++) {
          const slot = page.slots[slotIdx] || {};
          const col = slotIdx % 2;
          const row = Math.floor(slotIdx / 2);
          
          const slotX = margin + col * (imgWidth + 6);
          const slotY = yPos + row * slotHeight;
          
          // Slot label
          pdf.setFontSize(8);
          pdf.setTextColor(...COLOR_RED);
          pdf.text(`Photo ${slotIdx + 1}`, slotX, slotY);
          
          // Image box
          if (slot.image_data) {
            try {
              pdf.addImage(slot.image_data, 'JPEG', slotX, slotY + 2, imgWidth, imgHeight);
              // Subtle gray border around image
              pdf.setDrawColor(...COLOR_GRAY_300);
              pdf.rect(slotX, slotY + 2, imgWidth, imgHeight);
            } catch (e) {
              pdf.setDrawColor(...COLOR_GRAY_300);
              pdf.rect(slotX, slotY + 2, imgWidth, imgHeight);
              pdf.setFontSize(7);
              pdf.text('[Image error]', slotX + 35, slotY + 28);
            }
          } else {
            pdf.setDrawColor(...COLOR_GRAY_300);
            pdf.setFillColor(...COLOR_GRAY_100);
            pdf.rect(slotX, slotY + 2, imgWidth, imgHeight, 'FD');
            pdf.setFontSize(7);
            pdf.setTextColor(...COLOR_GRAY_500);
            pdf.text('No image', slotX + 38, slotY + 28);
          }
          
          // Description box
          const descY = slotY + imgHeight + 4;
          pdf.setDrawColor(...COLOR_GRAY_300);
          pdf.setFillColor(255, 255, 255);
          pdf.rect(slotX, descY, imgWidth, descHeight, 'FD');
          
          if (slot.description) {
            pdf.setFontSize(6.5);
            pdf.setTextColor(...COLOR_GRAY_900);
            const splitDesc = pdf.splitTextToSize(slot.description, imgWidth - 3);
            pdf.text(splitDesc.slice(0, 3), slotX + 1.5, descY + 3.5);
          }
          
          // Admin comment (in red — brand accent)
          if (slot.admin_comment) {
            pdf.setFontSize(5.5);
            pdf.setTextColor(...COLOR_RED);
            const adminText = `Admin: ${slot.admin_comment}`;
            const splitAdmin = pdf.splitTextToSize(adminText, imgWidth - 3);
            pdf.text(splitAdmin.slice(0, 2), slotX + 1.5, descY + 12);
          }
        }
      }
      
      // Save PDF with Building/Floor/Flat as filename
      const filename = `${formTitle.replace(/[^a-z0-9]/gi, '_')}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      pdf.save(filename);
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Export all images as a ZIP bundle
  const handleExportImages = async () => {
    try {
      // Collect all images from all pages
      const allImages = [];
      const formTitle = [
        building || form.building,
        floorNumber || form.floor_number,
        flatNumber || form.flat_number
      ].filter(Boolean).join('_') || form.trade_type;
      
      pages.forEach((page, pageIndex) => {
        page.slots?.forEach((slot, slotIndex) => {
          if (slot.image_data) {
            allImages.push({
              data: slot.image_data,
              name: `Page${pageIndex + 1}_Photo${slotIndex + 1}`,
              description: slot.description || ''
            });
          }
        });
      });
      
      if (allImages.length === 0) {
        toast.error('No images to export');
        return;
      }
      
      // If only one image, download directly
      if (allImages.length === 1) {
        const img = allImages[0];
        const link = document.createElement('a');
        link.href = img.data;
        link.download = `${formTitle}_${img.name}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Image downloaded');
        return;
      }
      
      // For multiple images, create a ZIP bundle
      const zip = new JSZip();
      
      allImages.forEach((img, index) => {
        // Convert base64 to blob
        const base64Data = img.data.split(',')[1];
        zip.file(`${img.name}.jpg`, base64Data, { base64: true });
      });
      
      const content = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(content);
      link.download = `${formTitle}_images_${format(new Date(), 'yyyyMMdd')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success(`${allImages.length} images exported as ZIP`);
    } catch (error) {
      console.error('Image export error:', error);
      toast.error('Failed to export images');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!form) {
    return null;
  }

  const statusInfo = statusConfig[form.status] || { label: form.status, class: 'status-draft', icon: FileText };
  const StatusIcon = statusInfo.icon;
  const editable = canEdit();

  return (
    <div className="min-h-screen bg-background" data-testid="form-editor-page">
      <Header />
      
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  {form.company_name || form.project_name}
                </h1>
                <Badge className={statusInfo.class}>
                  <StatusIcon className="h-3 w-3 mr-1" />
                  {statusInfo.label}
                </Badge>
              </div>
              <p className="text-muted-foreground mt-1">
                {form.trade_type}{form.form_type ? ` - ${form.form_type}` : ''} • {form.site_name || 'No site'} • Created by {form.created_by_name}
              </p>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button 
                variant="outline" 
                onClick={handleExportPdf}
                disabled={generatingPdf}
                data-testid="export-pdf-btn"
              >
                {generatingPdf ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Download PDF
              </Button>
              <Button 
                variant="outline" 
                onClick={handleExportImages}
                data-testid="export-images-btn"
              >
                <Images className="mr-2 h-4 w-4" />
                Export Photos
              </Button>
              {editable && (
                <>
                  <Button 
                    variant="outline" 
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="save-btn"
                  >
                    {saving ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    Save
                  </Button>
                  {!isAdminOrManager() && (form.status === 'draft' || form.status === 'returned') && (
                    <Button 
                      onClick={() => setShowSubmitDialog(true)}
                      className="bg-primary hover:bg-primary/90"
                      data-testid="submit-btn"
                    >
                      <Send className="mr-2 h-4 w-4" />
                      Submit
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
          
          {form.return_reason && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Form Returned</p>
                  <p className="text-sm text-amber-700 mt-1">{form.return_reason}</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Admin Actions for Submitted Forms */}
          {isAdminOrManager() && form.status === 'submitted' && (
            <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <h3 className="font-medium text-slate-800 mb-3">Admin Actions</h3>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => setShowApproveDialog(true)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="admin-approve-btn"
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Approve
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowReturnDialog(true)}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="admin-return-btn"
                >
                  <AlertTriangle className="mr-2 h-4 w-4" />
                  Send Back
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowRejectDialog(true)}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="admin-reject-btn"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Reject
                </Button>
              </div>
            </div>
          )}
          
          {offlineMode && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <WifiOff className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-800">Offline Mode</p>
                  <p className="text-sm text-blue-700 mt-1">Changes will be synced when you&apos;re back online</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Form Header Section */}
        <Card className="mb-6 border-slate-200 shadow-sm">
          <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="font-heading text-lg">Form Details</CardTitle>
                <CardDescription className="text-sm">Basic information about this QA inspection</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            {/* Read-only Info Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">Timestamp</Label>
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-md border">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <span className="text-sm font-medium">{format(new Date(form.timestamp), 'PPpp')}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">Trade Type</Label>
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-md border">
                  <Badge variant="secondary" className="font-medium">{form.trade_type}</Badge>
                </div>
              </div>
            </div>
            
            {/* Building, Floor, Flat Number Fields */}
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <h4 className="text-sm font-semibold text-blue-800 mb-4 flex items-center gap-2">
                <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                  <span className="text-blue-600 text-xs font-bold">#</span>
                </div>
                Location Details
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="building" className="text-blue-800 text-xs uppercase tracking-wide">Building</Label>
                  <Input
                    id="building"
                    placeholder="e.g., Block A"
                    value={building}
                    onChange={(e) => setBuilding(e.target.value)}
                    disabled={!editable}
                    className="bg-white border-blue-200 focus:border-blue-400"
                    data-testid="building-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="floor-number" className="text-blue-800 text-xs uppercase tracking-wide">Floor</Label>
                  <Input
                    id="floor-number"
                    placeholder="e.g., 3"
                    value={floorNumber}
                    onChange={(e) => setFloorNumber(e.target.value)}
                    disabled={!editable}
                    className="bg-white border-blue-200 focus:border-blue-400"
                    data-testid="floor-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="flat-number" className="text-blue-800 text-xs uppercase tracking-wide">Flat/Unit</Label>
                  <Input
                    id="flat-number"
                    placeholder="e.g., 12A"
                    value={flatNumber}
                    onChange={(e) => setFlatNumber(e.target.value)}
                    disabled={!editable}
                    className="bg-white border-blue-200 focus:border-blue-400"
                    data-testid="flat-number-input"
                  />
                </div>
              </div>
            </div>
            
            {/* Admin Controls for Submitted Form */}
            {isAdminOrManager() && form?.status !== 'draft' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="space-y-2">
                  <Label htmlFor="submitted-by" className="text-amber-800">Submitted By (Admin Edit)</Label>
                  <Input
                    id="submitted-by"
                    placeholder="Name of submitter"
                    value={submittedByName}
                    onChange={(e) => setSubmittedByName(e.target.value)}
                    className="bg-white"
                    data-testid="submitted-by-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="submitted-date" className="text-amber-800">Submission Date (Admin Edit)</Label>
                  <Input
                    id="submitted-date"
                    type="date"
                    value={submittedDate}
                    onChange={(e) => setSubmittedDate(e.target.value)}
                    className="bg-white"
                    data-testid="submitted-date-input"
                  />
                </div>
                <div className="sm:col-span-2 text-xs text-amber-700">
                  ⚠️ Admin can modify the submitter name, signature, and date on submitted forms
                </div>
              </div>
            )}
            
            {/* Inspector Details Section */}
            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
              <h4 className="text-sm font-semibold text-green-800 mb-4 flex items-center gap-2">
                <div className="w-6 h-6 bg-green-100 rounded flex items-center justify-center">
                  <CheckCircle className="h-3 w-3 text-green-600" />
                </div>
                Name
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="individual-name" className="text-green-800 text-xs uppercase tracking-wide">Name</Label>
                  <Input
                    id="individual-name"
                    placeholder="Enter your name"
                    value={individualName}
                    onChange={(e) => setIndividualName(e.target.value)}
                    disabled={!editable && !isAdminOrManager()}
                    className="bg-white border-green-200 focus:border-green-400"
                    data-testid="individual-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="individual-date" className="text-green-800 text-xs uppercase tracking-wide">Inspection Date</Label>
                  <Input
                    id="individual-date"
                    type="date"
                    value={individualDate}
                    onChange={(e) => setIndividualDate(e.target.value)}
                    disabled={!editable && !isAdminOrManager()}
                    className="bg-white border-green-200 focus:border-green-400"
                    data-testid="individual-date-input"
                  />
                </div>
              </div>
            </div>
            
            {/* Signature Section */}
            <div className="p-4 bg-slate-50 rounded-lg border">
              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                Digital Signature
              </h4>
              <SignaturePad
                ref={signatureRef}
                value={signatureData}
                onChange={setSignatureData}
                disabled={!editable}
                height={120}
              />
            </div>
            
            {/* Notes Section */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-xs text-muted-foreground uppercase tracking-wide">Additional Notes</Label>
              <Textarea
                id="notes"
                placeholder="Add any additional notes, observations, or comments..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={!editable}
                className="min-h-[100px] resize-none"
                data-testid="notes-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Checklist Section - First page of form */}
        <Card className="mb-6 border-slate-200 shadow-sm">
          <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <ClipboardList className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="font-heading text-lg">Requirements Checklist</CardTitle>
                  <CardDescription>
                    {isAdminOrManager() ? 'Define inspection requirements (25 items max)' : 'Review and mark checklist items'}
                  </CardDescription>
                </div>
              </div>
              {isAdminOrManager() && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowTemplateDialog(true)}
                  className="border-primary/30 text-primary hover:bg-primary/5"
                  data-testid="save-template-btn"
                >
                  <BookTemplate className="mr-2 h-4 w-4" />
                  Save as Template
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="border rounded-lg overflow-hidden shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-100">
                    <TableHead className="w-12 text-center font-semibold">#</TableHead>
                    <TableHead className="font-semibold">Requirement</TableHead>
                    <TableHead className="w-28 text-center font-semibold">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {checklist.items?.slice(0, 25).map((item, index) => (
                    <TableRow key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      <TableCell className="text-center font-medium text-slate-500">
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <Input
                          value={item?.requirement || ''}
                          onChange={(e) => handleChecklistChange(index, 'requirement', e.target.value)}
                          placeholder={isAdminOrManager() ? 'Enter requirement...' : ''}
                          disabled={!isAdminOrManager()}
                          className="border-0 bg-transparent focus:ring-0 h-8"
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            type="button"
                            variant={item?.status === '✓' ? 'default' : 'outline'}
                            size="sm"
                            className={`h-7 w-7 p-0 ${item?.status === '✓' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                            onClick={() => handleChecklistChange(index, 'status', item?.status === '✓' ? '' : '✓')}
                            disabled={!editable}
                            title="Pass"
                          >
                            ✓
                          </Button>
                          <Button
                            type="button"
                            variant={item?.status === '✗' ? 'default' : 'outline'}
                            size="sm"
                            className={`h-7 w-7 p-0 ${item?.status === '✗' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                            onClick={() => handleChecklistChange(index, 'status', item?.status === '✗' ? '' : '✗')}
                            disabled={!editable}
                            title="Fail"
                          >
                            ✗
                          </Button>
                          <Button
                            type="button"
                            variant={item?.status === 'N/A' ? 'default' : 'outline'}
                            size="sm"
                            className={`h-7 px-1 text-xs ${item?.status === 'N/A' ? 'bg-gray-500 hover:bg-gray-600' : ''}`}
                            onClick={() => handleChecklistChange(index, 'status', item?.status === 'N/A' ? '' : 'N/A')}
                            disabled={!editable}
                            title="Not Applicable"
                          >
                            NA
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Photo Slots - Dynamic Pages */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg">
                Inspection Photos ({pages.length} {pages.length === 1 ? 'page' : 'pages'})
              </CardTitle>
              {editable && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleAddPage}
                  disabled={addingPage || pages.length >= 20}
                  data-testid="add-page-btn"
                >
                  {addingPage ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="mr-2 h-4 w-4" />
                  )}
                  Add Page
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={currentPage} onValueChange={setCurrentPage}>
              {/* Scrollable tabs for many pages */}
              <div className="relative mb-6">
                <ScrollArea className="w-full whitespace-nowrap">
                  <TabsList className="inline-flex w-auto">
                    {pages.map((_, index) => (
                      <TabsTrigger 
                        key={index} 
                        value={String(index + 1)}
                        data-testid={`page-${index + 1}-tab`}
                        className="min-w-[80px]"
                      >
                        Page {index + 1}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
              </div>
              
              {pages.map((page, pageIndex) => (
                <TabsContent key={pageIndex} value={String(pageIndex + 1)}>
                  {/* Page actions */}
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      {editable && (
                        <GalleryUploader
                          onImagesSelected={(images) => handleGalleryUpload(pageIndex, images)}
                          maxImages={getEmptySlotsCount(pageIndex)}
                          disabled={!editable || getEmptySlotsCount(pageIndex) === 0}
                        />
                      )}
                      {getEmptySlotsCount(pageIndex) === 0 && editable && (
                        <span className="text-sm text-muted-foreground">All slots filled</span>
                      )}
                    </div>
                    
                    {editable && pages.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          setPageToRemove(pageIndex + 1);
                          setShowRemovePageDialog(true);
                        }}
                        data-testid={`remove-page-${pageIndex + 1}-btn`}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Remove Page {pageIndex + 1}
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {page.slots.map((slot, slotIndex) => (
                      <div key={slot.slot_id} className="space-y-2">
                        <Label className="text-sm font-medium text-muted-foreground">
                          Slot {slotIndex + 1}
                        </Label>
                        <PhotoSlot
                          slot={slot}
                          onImageChange={(imageData) => 
                            handleSlotImageChange(pageIndex, slotIndex, imageData)
                          }
                          onDescriptionChange={(description) => 
                            handleSlotDescriptionChange(pageIndex, slotIndex, description)
                          }
                          onAdminCommentChange={(comment) => 
                            handleAdminCommentChange(pageIndex, slotIndex, comment)
                          }
                          disabled={!editable}
                          isAdmin={isAdminOrManager()}
                          showAdminComment={isAdminOrManager() && form?.status !== 'draft'}
                        />
                      </div>
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      </main>

      {/* Submit Confirmation Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Form?</AlertDialogTitle>
            <AlertDialogDescription>
              Once submitted, you won&apos;t be able to edit this form unless an admin sends it back to you. 
              Make sure all information is correct.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-submit-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-primary hover:bg-primary/90"
              data-testid="confirm-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Form'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Page Confirmation Dialog */}
      <AlertDialog open={showRemovePageDialog} onOpenChange={setShowRemovePageDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Page {pageToRemove}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all photos and descriptions on this page. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-remove-page-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRemovePage}
              disabled={removingPage}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-remove-page-btn"
            >
              {removingPage ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove Page'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save as Template Dialog (Admin only) */}
      <AlertDialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <BookTemplate className="h-5 w-5 text-primary" />
              Save as Template
            </AlertDialogTitle>
            <AlertDialogDescription>
              Save this form as a template that can be issued to users at the same job location. 
              The checklist requirements will be included.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="template-name">Template Name</Label>
            <Input
              id="template-name"
              placeholder="e.g., Plumbing Inspection Template"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="mt-2"
              data-testid="template-name-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-template-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSaveAsTemplate}
              disabled={savingTemplate || !templateName.trim()}
              className="bg-primary hover:bg-primary/90"
              data-testid="confirm-template-btn"
            >
              {savingTemplate ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Template'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Admin Approve Dialog */}
      <AlertDialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              Approve Form
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to approve this form? The user will be notified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processingAction}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleApproveForm}
              disabled={processingAction}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-approve-btn"
            >
              {processingAction ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="mr-2 h-4 w-4" />
              )}
              Approve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Admin Return Dialog */}
      <AlertDialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Send Back to User
            </AlertDialogTitle>
            <AlertDialogDescription>
              Send this form back to the user for revisions. Please provide a reason.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="return-reason">Reason *</Label>
            <Textarea
              id="return-reason"
              placeholder="Please explain what needs to be changed..."
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              className="mt-2 min-h-[100px]"
              data-testid="return-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processingAction}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReturnForm}
              disabled={processingAction || !returnReason.trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-return-btn"
            >
              {processingAction ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <AlertTriangle className="mr-2 h-4 w-4" />
              )}
              Send Back
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Admin Reject Dialog */}
      <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Reject Form
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently reject this form. Please provide a reason.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="reject-reason">Reason *</Label>
            <Textarea
              id="reject-reason"
              placeholder="Please explain why this form is being rejected..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="mt-2 min-h-[100px]"
              data-testid="reject-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processingAction}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRejectForm}
              disabled={processingAction || !rejectReason.trim()}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-reject-btn"
            >
              {processingAction ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Reject Form
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
